import { useEffect } from 'react';
import './PlayRoughAnimation.css';

/**
 * PROFESSIONAL PLAY ROUGH ANIMATION
 * Fairy-type physical move with playful but rough contact effects
 * 
 * Key Features:
 * - Fairy-type physical attack (90 base power, 90% accuracy)
 * - 10% chance to lower opponent's Attack stat
 * - Playful fairy energy with rough physical contact
 * - Professional gaming company quality with cute but impactful timing
 * - Physical move animation with dash and contact mechanics
 */
export default function PlayRoughAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate trajectory for dash
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Playful Preparation (0-400ms)
    console.log('Play Rough Phase 1: Playful Preparation');
    
    // Player enhancement with playful aura
    playerEl.classList.add('play-rough-user-charge');
    
    // Fairy sparkle aura around player
    const fairyAura = document.createElement('div');
    fairyAura.className = 'play-rough-fairy-aura';
    fairyAura.style.left = `${playerX - 70}px`;
    fairyAura.style.top = `${playerY - 70}px`;
    container.appendChild(fairyAura);
    elements.push(fairyAura);

    // Playful sparkle particles around player
    for (let i = 0; i < 12; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'play-rough-sparkle-particle';
      const angle = (i / 12) * 360;
      const radius = 50 + (i % 3) * 10;
      const sparkleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const sparkleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      sparkle.style.left = `${sparkleX - 4}px`;
      sparkle.style.top = `${sparkleY - 4}px`;
      sparkle.style.animationDelay = `${i * 30}ms`;
      sparkle.textContent = '✨';
      container.appendChild(sparkle);
      elements.push(sparkle);
    }

    // Heart particles showing playful nature
    for (let i = 0; i < 6; i++) {
      const heart = document.createElement('div');
      heart.className = 'play-rough-heart-particle';
      const heartAngle = (i / 6) * 360;
      const heartRadius = 65;
      const heartX = playerX + Math.cos(heartAngle * Math.PI / 180) * heartRadius;
      const heartY = playerY + Math.sin(heartAngle * Math.PI / 180) * heartRadius;
      heart.style.left = `${heartX - 8}px`;
      heart.style.top = `${heartY - 8}px`;
      heart.style.animationDelay = `${i * 60}ms`;
      heart.textContent = '💖';
      container.appendChild(heart);
      elements.push(heart);
    }

    // PHASE 2: Playful Dash Attack (400-800ms)
    const phase2Timer = setTimeout(() => {
      console.log('Play Rough Phase 2: Playful Dash Attack');
      
      // Player dash movement toward opponent
      playerEl.classList.add('play-rough-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.7}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.7}px`);

      // Fairy energy trail during dash
      const fairyTrail = document.createElement('div');
      fairyTrail.className = 'play-rough-fairy-trail';
      fairyTrail.style.left = `${playerX - 10}px`;
      fairyTrail.style.top = `${playerY - 10}px`;
      fairyTrail.style.width = `${distance * 0.7}px`;
      fairyTrail.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`;
      fairyTrail.style.transformOrigin = 'left center';
      container.appendChild(fairyTrail);
      elements.push(fairyTrail);

      // Speed sparkles during dash
      for (let i = 0; i < 8; i++) {
        const speedTimer = setTimeout(() => {
          const speedSparkle = document.createElement('div');
          speedSparkle.className = 'play-rough-speed-sparkle';
          const sparkleX = playerX + (dx * 0.7 * (i / 8)) + (Math.random() - 0.5) * 30;
          const sparkleY = playerY + (dy * 0.7 * (i / 8)) + (Math.random() - 0.5) * 30;
          speedSparkle.style.left = `${sparkleX - 3}px`;
          speedSparkle.style.top = `${sparkleY - 3}px`;
          speedSparkle.textContent = '⭐';
          container.appendChild(speedSparkle);
          elements.push(speedSparkle);
        }, i * 40);
        elements.push({ timer: speedTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Rough Contact Impact (800-1400ms)
    const phase3Timer = setTimeout(() => {
      console.log('Play Rough Phase 3: Rough Contact Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('play-rough-opponent-hit');

      // Reset player position
      playerEl.classList.remove('play-rough-user-dash');
      playerEl.classList.add('play-rough-user-contact');

      // Massive fairy impact explosion
      const fairyImpact = document.createElement('div');
      fairyImpact.className = 'play-rough-fairy-impact';
      fairyImpact.style.left = `${opponentX - 100}px`;
      fairyImpact.style.top = `${opponentY - 100}px`;
      container.appendChild(fairyImpact);
      elements.push(fairyImpact);

      // Impact sparkle burst
      for (let i = 0; i < 16; i++) {
        const impactSparkle = document.createElement('div');
        impactSparkle.className = 'play-rough-impact-sparkle';
        const sparkleAngle = (i / 16) * 360;
        const sparkleRadius = 60 + Math.random() * 40;
        const sparkleX = opponentX + Math.cos(sparkleAngle * Math.PI / 180) * sparkleRadius;
        const sparkleY = opponentY + Math.sin(sparkleAngle * Math.PI / 180) * sparkleRadius;
        impactSparkle.style.left = `${sparkleX - 4}px`;
        impactSparkle.style.top = `${sparkleY - 4}px`;
        impactSparkle.style.animationDelay = `${i * 20}ms`;
        impactSparkle.style.setProperty('--scatter-x', `${Math.cos(sparkleAngle * Math.PI / 180) * 30}px`);
        impactSparkle.style.setProperty('--scatter-y', `${Math.sin(sparkleAngle * Math.PI / 180) * 30}px`);
        impactSparkle.textContent = '✨';
        container.appendChild(impactSparkle);
        elements.push(impactSparkle);
      }

      // Fairy energy shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'play-rough-shockwave';
          shockwave.style.left = `${opponentX - 60}px`;
          shockwave.style.top = `${opponentY - 60}px`;
          shockwave.style.animationDelay = `${i * 80}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 100);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Playful but rough impact hearts
      for (let i = 0; i < 8; i++) {
        const impactHeart = document.createElement('div');
        impactHeart.className = 'play-rough-impact-heart';
        const heartAngle = (i / 8) * 360;
        const heartRadius = 80;
        const heartX = opponentX + Math.cos(heartAngle * Math.PI / 180) * heartRadius;
        const heartY = opponentY + Math.sin(heartAngle * Math.PI / 180) * heartRadius;
        impactHeart.style.left = `${heartX - 6}px`;
        impactHeart.style.top = `${heartY - 6}px`;
        impactHeart.style.animationDelay = `${i * 40}ms`;
        impactHeart.textContent = '💕';
        container.appendChild(impactHeart);
        elements.push(impactHeart);
      }
    }, 800);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Attack Reduction Effect (1400-1800ms)
    const phase4Timer = setTimeout(() => {
      console.log('Play Rough Phase 4: Attack Reduction Effect');
      
      // Remove charge and contact effects
      playerEl.classList.remove('play-rough-user-charge', 'play-rough-user-contact');
      
      // Attack reduction visual effects (10% chance)
      const debuffAura = document.createElement('div');
      debuffAura.className = 'play-rough-debuff-aura';
      debuffAura.style.left = `${opponentX - 60}px`;
      debuffAura.style.top = `${opponentY - 60}px`;
      container.appendChild(debuffAura);
      elements.push(debuffAura);

      // Weakening sparkles around opponent
      for (let i = 0; i < 8; i++) {
        const weakenSparkle = document.createElement('div');
        weakenSparkle.className = 'play-rough-weaken-sparkle';
        const angle = (i / 8) * 360;
        const radius = 45;
        const sparkleX = opponentX + Math.cos(angle * Math.PI / 180) * radius;
        const sparkleY = opponentY + Math.sin(angle * Math.PI / 180) * radius;
        weakenSparkle.style.left = `${sparkleX - 3}px`;
        weakenSparkle.style.top = `${sparkleY - 3}px`;
        weakenSparkle.style.animationDelay = `${i * 50}ms`;
        weakenSparkle.textContent = '💫';
        container.appendChild(weakenSparkle);
        elements.push(weakenSparkle);
      }
    }, 1400);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Play Rough: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('play-rough-user-charge', 'play-rough-user-dash', 'play-rough-user-contact');
      opponentEl.classList.remove('play-rough-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1800);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('play-rough-user-charge', 'play-rough-user-dash', 'play-rough-user-contact');
      opponentEl.classList.remove('play-rough-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
PlayRoughAnimation.moveType = 'fairy';
